/**
 * OSS policy 上传：允许的文件类型与大小（与后端 /file/policyPostUpload 规则对齐）
 */
export const UPLOAD_POLICY = {
  image: {
    contentTypes: [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp'
    ],
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],
    maxSizeBytes: 10485760 // 10MB
  },
  file: {
    contentTypes: [
      'application/pdf',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/csv'
    ],
    extensions: ['pdf', 'xls', 'xlsx', 'csv'],
    maxSizeBytes: 52428800 // 50MB
  }
}

const EXT_TO_CONTENT_TYPE = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv'
}

const CONTENT_TYPE_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'application/pdf': 'pdf',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/csv': 'csv',
  'application/csv': 'csv'
}

const ALL_CONTENT_TYPES = Object.keys(UPLOAD_POLICY).reduce((list, key) => {
  return list.concat(UPLOAD_POLICY[key].contentTypes)
}, [])

export function getFileExtension(fileName = '') {
  const idx = fileName.lastIndexOf('.')
  if (idx < 0) return ''
  return fileName.slice(idx + 1).toLowerCase()
}

/**
 * 解析用于请求 policy 的 contentType
 * 优先使用白名单内的 file.type；否则按后缀推断（兼容 csv 报 text/plain 等）
 */
export function resolveContentType(file) {
  const rawType = (file && file.type ? file.type : '').toLowerCase().trim()
  if (rawType && ALL_CONTENT_TYPES.includes(rawType)) {
    return rawType
  }
  const ext = getFileExtension(file && file.name)
  return EXT_TO_CONTENT_TYPE[ext] || rawType || ''
}

/**
 * 生成带后缀的 realName（优先原始文件名；无后缀时按 contentType 补全）
 */
export function buildUploadRealName(file, contentType) {
  const original = (file && file.name) || ''
  const extFromName = getFileExtension(original)
  if (original && extFromName) {
    return original
  }
  const type = (contentType || resolveContentType(file) || '').toLowerCase()
  const ext = CONTENT_TYPE_TO_EXT[type] || 'jpg'
  return `${Date.now()}.${ext}`
}

function matchCategory(contentType, ext, forceCategory) {
  const categories = forceCategory
    ? [forceCategory].filter(key => UPLOAD_POLICY[key])
    : Object.keys(UPLOAD_POLICY)

  for (let i = 0; i < categories.length; i++) {
    const key = categories[i]
    const policy = UPLOAD_POLICY[key]
    const typeOk = contentType && policy.contentTypes.includes(contentType)
    const extOk = ext && policy.extensions.includes(ext)
    if (typeOk || extOk) {
      // 类型与后缀都存在时需同时合法，避免伪装扩展名
      if (contentType && ext && !(typeOk && extOk)) {
        continue
      }
      return key
    }
  }
  return null
}

/**
 * 校验上传文件类型与大小
 * @param {File} file
 * @param {{ category?: 'image'|'file' }} [options] 限定类别（图片上传场景传 image）
 * @returns {{ valid: boolean, contentType: string, category: string|null, message: string }}
 */
export function validateUploadFile(file, options = {}) {
  if (!file) {
    return {
      valid: false,
      contentType: '',
      category: null,
      message: '文件不存在'
    }
  }

  const forceCategory = options.category || null
  const contentType = resolveContentType(file)
  const ext = getFileExtension(file.name)
  const category = matchCategory(contentType, ext, forceCategory)

  if (!category) {
    return {
      valid: false,
      contentType,
      category: null,
      message: '文件格式不正确'
    }
  }

  const { maxSizeBytes } = UPLOAD_POLICY[category]
  if (typeof file.size === 'number' && file.size > maxSizeBytes) {
    const maxMb = Math.floor(maxSizeBytes / 1024 / 1024)
    return {
      valid: false,
      contentType,
      category,
      message: `文件大小超出限制（最大 ${maxMb}MB）`
    }
  }

  return {
    valid: true,
    contentType,
    category,
    message: ''
  }
}
